import { useCallback, useEffect, useRef, useState } from 'react';

import { toast } from 'sonner';
import { useOptionalVmClient } from '../api/context';
import { ApiError, PaymentRequiredError } from '../api/client';
import { isConsoleResponse, isNewTabResponse, isToastResponse } from '../api/types';
import { useImmediateResult } from '@/components/ImmediateResult';
import type {
  AgentCompleteEvent,
  AgentErrorEvent,
  AgentSpawnEvent,
  AuthRequestEvent,
  BridgeEvent,
  ChatPostResponse,
  CodeEndEvent,
  CodeOutputEvent,
  CodeStartEvent,
  RuntimeStateEvent,
  SubagentTextDeltaEvent,
  UserDownloadEvent,
  UserMessageEvent,
  WorkingStatusEvent,
} from '../api/types';
import type { AgentCanvasChatContext } from '@/artifact/types';
import { useChatStore } from './useChatStore';
import type { ChatState, ChatStoreOpts, CodeTimelineItem, ConsoleTimelineItem, TimelineItem } from './useChatStore';
import { trackAgentError } from '@/analytics';

export type {
  TimelineItem,
  UserTimelineItem,
  UserAttachment,
  AgentTimelineItem,
  CodeTimelineItem,
  SystemTimelineItem,
  DownloadTimelineItem,
  SubagentTimelineItem,
  SubagentConversationTurn,
  AuthRequestTimelineItem,
  ConsoleTimelineItem,
  ChatError,
  ChatState,
} from './useChatStore';

// ---------------------------------------------------------------------------
// Pure helpers (unchanged from previous implementation)
// ---------------------------------------------------------------------------

function findLastCodeIdx(timeline: TimelineItem[], execId: string): number {
  for (let i = timeline.length - 1; i >= 0; i--) {
    const item = timeline[i];
    if (item.kind === 'code' && item.execId === execId) return i;
  }
  return -1;
}

function upsertCodeOnChat(
  chat: ChatState,
  event: CodeStartEvent | CodeOutputEvent | CodeEndEvent,
) {
  const timeline = chat.timeline;
  const idx = findLastCodeIdx(timeline, event.id);

  if (event.type === 'code_start') {
    const codeItem: TimelineItem = {
      kind: 'code',
      id: `${event.run_id}:${event.id}:${Date.now()}`,
      execId: event.id,
      code: event.code,
      output: '',
      outputType: 'text',
      metadata: {},
      status: 'running',
    };
    if (idx === -1) {
      chat.timeline = [...timeline, codeItem];
      return;
    }
    const existing = timeline[idx];
    if (existing.kind === 'code' && existing.status !== 'running') {
      chat.timeline = [...timeline, codeItem];
      return;
    }
    const copy = [...timeline];
    if (copy[idx]?.kind === 'code') {
      const current = copy[idx] as CodeTimelineItem;
      copy[idx] = { ...current, code: event.code || current.code };
    }
    chat.timeline = copy;
    return;
  }

  if (idx === -1) return;
  const current = timeline[idx];
  if (current.kind !== 'code') return;

  const copy = [...timeline];
  if (event.type === 'code_output') {
    copy[idx] = {
      ...current,
      code: event.code?.trim() ? event.code : current.code,
      output: event.output,
      outputType: event.output_type,
      metadata: event.metadata ?? {},
    };
  } else if (event.type === 'code_end') {
    copy[idx] = { ...current, status: event.status };
  }
  chat.timeline = copy;
}

function ensureAgentMessageId(chat: ChatState, runId: string): string {
  if (
    chat.agentMessageId &&
    chat.agentMessageId.startsWith(`${runId}:`)
  ) {
    return chat.agentMessageId;
  }
  const id = `${runId}:agent:${Date.now()}`;
  chat.agentMessageId = id;
  return id;
}

function flushBufferedAgentText(chat: ChatState, runId: string) {
  const deltaContent = chat.bufferedAgentTextDelta;
  if (!deltaContent) return;

  const isRenderableText = (v: string) => v.trim().length > 0;
  const id = ensureAgentMessageId(chat, runId);
  const idx = chat.timeline.findIndex(
    (item) => item.kind === 'agent' && item.id === id,
  );

  if (idx === -1) {
    if (isRenderableText(deltaContent)) {
      chat.timeline = [
        ...chat.timeline,
        { kind: 'agent', id, text: deltaContent },
      ];
    }
  } else {
    const copy = [...chat.timeline];
    const item = copy[idx];
    if (item.kind === 'agent') {
      copy[idx] = { ...item, text: `${item.text}${deltaContent}` };
    }
    chat.timeline = copy;
  }

  chat.bufferedAgentTextDelta = '';
}

function findSubagentIdx(timeline: TimelineItem[], agentId: number): number {
  for (let i = timeline.length - 1; i >= 0; i--) {
    const item = timeline[i];
    if (item.kind === 'subagent' && item.agentId === agentId) return i;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Per-session SSE connection manager — lives outside React to avoid closure
// staleness.  One long-lived stream per session that receives events from
// *all* runs (current and future).  No reconnection needed on new messages.
// ---------------------------------------------------------------------------

const _sseAborts = new Map<string, AbortController>();

function disconnectSSE(sessionId: string) {
  const ac = _sseAborts.get(sessionId);
  if (ac) {
    ac.abort();
    _sseAborts.delete(sessionId);
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseChatInput {
  currentSessionId: string | null;
  onError?: (errorMessage: string) => void;
  onSessionGone?: (sessionId: string) => void;
  onSessionCreated?: (sessionId: string) => void;
}

export function useChat(input: UseChatInput) {
  const { currentSessionId } = input;
  const immediateResult = useImmediateResult();
  const client = useOptionalVmClient();
  const {
    getChat,
    getOrCreateChat,
    hydrateChat,
    removeChat,
    bump,
  } = useChatStore();
  const immediateResultRef = useRef(immediateResult);
  immediateResultRef.current = immediateResult;

  const currentSessionIdRef = useRef(currentSessionId);
  currentSessionIdRef.current = currentSessionId;

  const activeChatId = currentSessionId;
  const activeChat = currentSessionId ? getChat(currentSessionId) ?? null : null;

  const pendingModelRef = useRef<string | null>(null);
  const sendInFlightRef = useRef(false);
  const [sendInFlight, setSendInFlight] = useState(false);
  const onErrorRef = useRef(input.onError);
  onErrorRef.current = input.onError;
  const onSessionGoneRef = useRef(input.onSessionGone);
  onSessionGoneRef.current = input.onSessionGone;
  const onSessionCreatedRef = useRef(input.onSessionCreated);
  onSessionCreatedRef.current = input.onSessionCreated;

  const clientRef = useRef(client);
  clientRef.current = client;

  const getOrCreateChatRef = useRef(getOrCreateChat);
  getOrCreateChatRef.current = getOrCreateChat;
  const removeChatRef = useRef(removeChat);
  removeChatRef.current = removeChat;

  const bumpIfCurrent = useCallback(
    (sessionId: string) => {
      if (sessionId === currentSessionIdRef.current) bump();
    },
    [bump],
  );

  // -----------------------------------------------------------------------
  // Event handler — maps BridgeEvents to ChatState mutations.
  // -----------------------------------------------------------------------

  const onEvent = useCallback(
    (event: BridgeEvent) => {
      const chat = getOrCreateChat(event.session_id);
      const isRenderableText = (value: string) => value.trim().length > 0;
      const isTerminal = event.type === 'done' || event.type === 'cancelled';

      if (chat.runId && chat.runId !== event.run_id) {
        if (isTerminal) {
          return;
        }
        chat.isRunning = false;
        chat.runId = event.run_id;
        chat.seq = -1;
        chat.agentMessageId = null;
        chat.runningCodeExecIds.clear();
        chat.bufferedAgentTextDelta = '';
        chat.workingStatus = '';
        chat.isStreamingAgentText = false;
      } else if (!chat.runId) {
        chat.runId = event.run_id;
      }

      if (event.seq <= chat.seq) return;
      chat.seq = event.seq;

      if (!chat.isRunning && !isTerminal) {
        chat.isRunning = true;
      }

      // Any non-text-delta event means the agent is not *currently*
      // growing a visible text bubble — reset so the WorkingBubble can
      // reappear (e.g. during code-block generation or between turns).
      // `text_delta` itself overrides this back to true when it actually
      // appends renderable content below.
      if (event.type !== 'text_delta') {
        chat.isStreamingAgentText = false;
      }

      switch (event.type) {
        case 'user_message': {
          const um = event as UserMessageEvent;

          if (um.source === 'self') {
            // Agent-initiated run — show as system message, not a user bubble.
            const selfId = `${event.run_id}:user`;
            const existingSelf = chat.timeline.findIndex(
              (item) => item.kind === 'system' && item.id === selfId,
            );
            if (existingSelf !== -1) break;
            chat.timeline = [...chat.timeline, {
              kind: 'system',
              id: selfId,
              text: 'Agent is working on a self-initiated task…',
            }];
            break;
          }

          const userItem: TimelineItem = {
            kind: 'user',
            id: `${event.run_id}:user`,
            text: um.content,
          };
          if (um.file_paths?.length) {
            userItem.attachments = um.file_paths.map((p) => ({
              filename: p.split('/').pop() || p,
              path: p,
            }));
          }
          const existingIdx = chat.timeline.findIndex(
            (item) => item.kind === 'user' && item.id === userItem.id,
          );
          if (existingIdx !== -1) break;

          chat.timeline = [...chat.timeline, userItem];
          break;
        }

        case 'text_delta': {
          if (chat.runningCodeExecIds.size > 0) {
            // Text that arrives while a code block is running is held
            // back until `code_end` flushes it — the agent bubble does
            // not grow yet, so the WorkingBubble should stay visible.
            chat.bufferedAgentTextDelta += event.content;
            chat.isStreamingAgentText = false;
            break;
          }
          const id = ensureAgentMessageId(chat, event.run_id);
          const idx = chat.timeline.findIndex(
            (item) => item.kind === 'agent' && item.id === id,
          );
          let applied = false;
          if (idx === -1) {
            if (isRenderableText(event.content)) {
              chat.timeline = [
                ...chat.timeline,
                { kind: 'agent', id, text: event.content },
              ];
              applied = true;
            }
          } else {
            const copy = [...chat.timeline];
            const item = copy[idx];
            if (item.kind === 'agent') {
              copy[idx] = {
                ...item,
                text: item.text + event.content,
              };
              applied = true;
            }
            chat.timeline = copy;
          }
          // Only suppress the WorkingBubble when we actually grew a
          // visible agent bubble — whitespace-only deltas that are
          // filtered out shouldn't hide the "Working…" indicator.
          chat.isStreamingAgentText = applied;
          break;
        }

        case 'code_start':
        case 'code_output':
        case 'code_end': {
          if (event.type === 'code_start') {
            chat.agentMessageId = null;
            chat.runningCodeExecIds.add(event.id);
          } else if (event.type === 'code_end') {
            chat.runningCodeExecIds.delete(event.id);
          }
          upsertCodeOnChat(chat, event);
          if (
            event.type === 'code_end' &&
            chat.runningCodeExecIds.size === 0
          ) {
            flushBufferedAgentText(chat, event.run_id);
          }
          break;
        }

        case 'runtime_state': {
          const rtEvent = event as RuntimeStateEvent;
          chat.runtimeState = {
            runtimeId: rtEvent.runtime_id,
            mode: rtEvent.mode,
            startedAt: rtEvent.started_at,
          };
          break;
        }

        case 'agent_spawn': {
          const spawn = event as AgentSpawnEvent;
          const idx = findSubagentIdx(chat.timeline, spawn.id);
          if (idx === -1) {
            chat.timeline = [
              ...chat.timeline,
              {
                kind: 'subagent',
                id: `subagent-${spawn.id}`,
                agentId: spawn.id,
                parentId: spawn.parent_id,
                task: spawn.task,
                status: 'running',
                workingStatus: '',
                resultSummary: '',
                conversation: `\n\n### Run ${event.run_id.slice(0, 8)}\n**User task:** ${spawn.task}\n`,
                conversationTurns: [{ role: 'user', text: spawn.task, runId: event.run_id }],
                lastRunId: event.run_id,
              },
            ];
          } else {
            const copy = [...chat.timeline];
            const item = copy[idx];
            if (item.kind === 'subagent') {
              const turns = [...item.conversationTurns];
              const last = turns[turns.length - 1];
              if (!last || last.role !== 'user' || last.runId !== event.run_id || last.text !== spawn.task) {
                turns.push({ role: 'user', text: spawn.task, runId: event.run_id });
              }
              copy[idx] = {
                ...item,
                parentId: spawn.parent_id ?? item.parentId,
                task: spawn.task,
                status: 'running',
                workingStatus: '',
                resultSummary: '',
                conversation: `${item.conversation}\n\n### Run ${event.run_id.slice(0, 8)}\n**User task:** ${spawn.task}\n`,
                conversationTurns: turns,
                lastRunId: event.run_id,
              };
            }
            chat.timeline = copy;
          }
          break;
        }

        case 'subagent_text_delta': {
          const delta = event as SubagentTextDeltaEvent;
          const idx = findSubagentIdx(chat.timeline, delta.agent_id);
          if (idx !== -1) {
            const copy = [...chat.timeline];
            const item = copy[idx];
            if (item.kind === 'subagent') {
              if (!delta.content.trim()) {
                copy[idx] = { ...item, lastRunId: event.run_id };
                chat.timeline = copy;
                break;
              }
              const normalizedRole =
                delta.role === 'user' ? 'user' : delta.role === 'system' ? 'system' : 'agent';
              if (normalizedRole === 'user') {
                copy[idx] = {
                  ...item,
                  lastRunId: event.run_id,
                };
                chat.timeline = copy;
                break;
              }
              const turns = [...item.conversationTurns];
              const last = turns[turns.length - 1];
              if (last && last.role === normalizedRole && last.runId === event.run_id) {
                turns[turns.length - 1] = { ...last, text: last.text + delta.content };
              } else {
                turns.push({ role: normalizedRole, text: delta.content, runId: event.run_id });
              }
              copy[idx] = {
                ...item,
                conversation: item.conversation + delta.content,
                conversationTurns: turns,
                lastRunId: event.run_id,
              };
            }
            chat.timeline = copy;
          }
          break;
        }

        case 'agent_complete': {
          const done = event as AgentCompleteEvent;
          const idx = findSubagentIdx(chat.timeline, done.id);
          if (idx !== -1) {
            const copy = [...chat.timeline];
            const item = copy[idx];
            if (item.kind === 'subagent') {
              const turns = [...item.conversationTurns];
              const summary = (done.result_summary ?? '').trim();
              const lastIdx = turns.length - 1;
              const last = lastIdx >= 0 ? turns[lastIdx] : null;
              if (summary) {
                if (last && last.role === 'agent' && last.runId === event.run_id) {
                  const lastText = last.text.trim();
                  if (summary === lastText || summary.includes(lastText)) {
                    turns[lastIdx] = { ...last, text: summary };
                  } else {
                    turns.push({ role: 'agent', text: summary, runId: event.run_id });
                  }
                } else {
                  turns.push({ role: 'agent', text: summary, runId: event.run_id });
                }
              }
              copy[idx] = {
                ...item,
                status: 'done',
                workingStatus: '',
                resultSummary: done.result_summary,
                conversationTurns: turns,
                lastRunId: event.run_id,
              };
            }
            chat.timeline = copy;
          }
          break;
        }

        case 'agent_error': {
          const errEvt = event as AgentErrorEvent;
          trackAgentError(errEvt.error);
          if (errEvt.id != null) {
            const idx = findSubagentIdx(chat.timeline, errEvt.id);
            if (idx !== -1) {
              const copy = [...chat.timeline];
              const item = copy[idx];
              if (item.kind === 'subagent') {
                copy[idx] = {
                  ...item,
                  status: 'error',
                  workingStatus: '',
                  resultSummary: errEvt.error,
                  conversationTurns: [
                    ...item.conversationTurns,
                    { role: 'system', text: errEvt.error, runId: event.run_id },
                  ],
                  lastRunId: event.run_id,
                };
              }
              chat.timeline = copy;
            }
          }
          if (/reserve enough balance|ran out of balance/i.test(errEvt.error)) {
            chat.error = { type: 'payment-required' };
          } else {
            console.warn('[chat] agent_error:', errEvt.error);
            chat.error = { type: 'unknown', message: errEvt.error };
          }
          break;
        }

        case 'user_download': {
          const dl = event as UserDownloadEvent;
          chat.timeline = [
            ...chat.timeline,
            {
              kind: 'download',
              id: `${event.run_id}:download:${dl.download_id}`,
              downloadId: dl.download_id,
              filename: dl.filename,
              size: dl.size,
              label: dl.label,
            },
          ];
          break;
        }

        case 'auth_request': {
          const authEvt = event as AuthRequestEvent;
          const authId = `${event.run_id}:auth:${authEvt.provider}`;
          const existingAuthIdx = chat.timeline.findIndex(
            (item) => item.kind === 'auth_request' && item.id === authId,
          );
          if (existingAuthIdx === -1) {
            chat.timeline = [
              ...chat.timeline,
              {
                kind: 'auth_request',
                id: authId,
                provider: authEvt.provider,
                displayName: authEvt.display_name || authEvt.provider,
                iconUrl: authEvt.icon_url || '',
                authStartUrl: authEvt.auth_start_url,
                status: 'pending',
              },
            ];
          }
          break;
        }

        case 'auth_resolved': {
          const provider = (event as any).provider as string;
          chat.timeline = chat.timeline.map((item) => {
            if (item.kind === 'auth_request' && item.provider === provider && item.status === 'pending') {
              return { ...item, status: 'success' as const };
            }
            return item;
          });
          break;
        }

        case 'working_status': {
          const statusEvent = event as WorkingStatusEvent;
          const agentId = statusEvent.agent_id ?? null;
          if (agentId != null && agentId > 0) {
            const idx = findSubagentIdx(chat.timeline, agentId);
            if (idx !== -1) {
              const copy = [...chat.timeline];
              const item = copy[idx];
              if (item.kind === 'subagent') {
                copy[idx] = {
                  ...item,
                  status: item.status === 'done' ? item.status : 'running',
                  workingStatus: statusEvent.status,
                  lastRunId: event.run_id,
                };
              }
              chat.timeline = copy;
            }
          } else {
            chat.workingStatus = statusEvent.status;
          }
          break;
        }

        case 'cancelled': {
          chat.isRunning = false;
          chat.agentMessageId = null;
          chat.runningCodeExecIds.clear();
          chat.bufferedAgentTextDelta = '';
          chat.workingStatus = '';
          chat.isStreamingAgentText = false;
          break;
        }

        case 'done': {
          flushBufferedAgentText(chat, event.run_id);
          let changed = false;
          const mapped = chat.timeline.map((item) => {
            if (
              item.kind === 'subagent' &&
              item.status === 'running' &&
              item.lastRunId === event.run_id
            ) {
              changed = true;
              return { ...item, status: 'done' as const, workingStatus: '' };
            }
            if (item.kind === 'auth_request' && item.status === 'pending') {
              changed = true;
              return { ...item, status: 'success' as const };
            }
            return item;
          });
          if (changed) chat.timeline = mapped;

          chat.isRunning = false;
          chat.agentMessageId = null;
          chat.runningCodeExecIds.clear();
          chat.bufferedAgentTextDelta = '';
          chat.workingStatus = '';
          chat.isStreamingAgentText = false;
          break;
        }
      }

      bumpIfCurrent(event.session_id);
    },
    [getOrCreateChat, bumpIfCurrent],
  );

  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  // -----------------------------------------------------------------------
  // connectToSession — opens per-session SSE to
  // GET /api/sessions/{sessionId}/events.  The stream receives events from
  // all runs on this session (current + future), so no reconnection is
  // needed when a follow-up message starts a new run.
  // Auto-reconnects on drop with exponential backoff (500ms → 10s cap).
  // -----------------------------------------------------------------------

  const connectToSession = useCallback(
    (sessionId: string, afterSeq: number = -1) => {
      if (!clientRef.current) return;
      if (_sseAborts.has(sessionId)) return;

      const ac = new AbortController();
      _sseAborts.set(sessionId, ac);

      let currentSeq = afterSeq;
      let currentRunId: string | null = null;
      let backoff = 500;

      const stream = async () => {
        while (!ac.signal.aborted) {
          const client = clientRef.current;
          if (!client) return;
          try {
            await client.streamSessionContentEvents(
              sessionId,
              currentSeq,
              (event) => {
                if (ac.signal.aborted) return;
                if (currentRunId && event.run_id !== currentRunId) {
                  currentSeq = -1;
                }
                currentRunId = event.run_id;
                if (event.seq > currentSeq) currentSeq = event.seq;
                onEventRef.current(event);
              },
              ac.signal,
            );
            backoff = 500;
          } catch (err) {
            if (ac.signal.aborted) return;
            if (err instanceof DOMException && err.name === 'AbortError') return;
            if (err instanceof ApiError && err.status === 404) {
              _sseAborts.delete(sessionId);
              removeChatRef.current(sessionId);
              onSessionGoneRef.current?.(sessionId);
              return;
            }
          }

          if (ac.signal.aborted) return;

          const chatState = getOrCreateChatRef.current(sessionId);
          if (!chatState.isRunning) {
            currentSeq = -1;
          }

          await new Promise<void>((resolve) => {
            const timer = setTimeout(resolve, backoff);
            ac.signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
          });
          backoff = Math.min(backoff * 2, 10_000);
        }

        if (_sseAborts.get(sessionId) === ac) {
          _sseAborts.delete(sessionId);
        }
      };

      stream();
    },
    [],
  );

  const connectToSessionRef = useRef(connectToSession);
  connectToSessionRef.current = connectToSession;

  // -----------------------------------------------------------------------
  // sendMessage — POST /api/chat → JSON; the per-session SSE stream
  // delivers run events automatically.  Server creates the session if
  // needed and emits user_message as the first event.
  // -----------------------------------------------------------------------

  const sendMessage = useCallback(
    (
      message: string,
      filePaths?: string[],
      opts?: {
        agentCanvas?: AgentCanvasChatContext | null;
        onStarted?: () => void;
      },
    ) => {
      const trimmed = message.trim();
      if (!trimmed) return;
      if (!client) return;
      if (sendInFlightRef.current) {
        // Silent drop — shouldn't happen in normal flow. If a send gets
        // stuck because a previous request failed without resetting the
        // ref, this is where the message disappears.
        console.debug('[sendMessage] dropped: another send already in flight');
        return;
      }

      const sessionId = currentSessionIdRef.current;
      const chat = sessionId ? getOrCreateChat(sessionId) : null;
      const model = chat?.model ?? pendingModelRef.current ?? undefined;

      if (chat) {
        chat.error = null;
        bump();
      }

      sendInFlightRef.current = true;
      setSendInFlight(true);

      void (async () => {
        let result: ChatPostResponse;
        try {
          result = await client.startRun(trimmed, sessionId, {
            model,
            filePaths,
            agentCanvas: opts?.agentCanvas ?? null,
          });
        } catch (err) {
          sendInFlightRef.current = false;
          setSendInFlight(false);
          if (err instanceof DOMException && err.name === 'AbortError') return;
          let msg: string;
          if (err instanceof ApiError) {
            // Server responded — its JSON `detail` is already in err.message,
            // and for unhandled 5xx includes a correlation id (ref xxxxxxxx)
            // that can be grepped against the server log.
            console.warn('[api] /api/chat returned', err.status, err.message);
            // In local dev the server ships the exception type + traceback
            // in the response body; surface it in the console so we don't
            // have to tab over to the server log.
            const tb = err.body?.traceback;
            const excType = err.body?.exception;
            if (typeof excType === 'string') console.warn('[api] server exception:', excType);
            if (typeof tb === 'string') console.warn('[api] server traceback:\n' + tb);
            const hasDetails = typeof tb === 'string' || typeof excType === 'string';
            msg = `Server error ${err.status}: ${err.message}`
              + (hasDetails ? ' — see dev console for details.' : '');
          } else if (err instanceof TypeError && /network|fetch|failed to fetch/i.test(err.message)) {
            msg = 'Unable to reach the server. Check your connection and try again.';
          } else if (err instanceof Error) {
            msg = err.message;
          } else {
            msg = String(err);
          }
          if (chat) {
            if (err instanceof PaymentRequiredError) {
              chat.error = { type: 'payment-required' };
            } else {
              console.warn('[chat] send error:', msg);
              chat.error = { type: 'unknown', message: msg };
            }
            bumpIfCurrent(chat.sessionId);
          } else {
            onErrorRef.current?.(msg);
          }
          return;
        }

        sendInFlightRef.current = false;
        setSendInFlight(false);
        opts?.onStarted?.();

        // Server-side dev-command intercept: the server returned a
        // non-chat response shape instead of starting a run.
        if (isNewTabResponse(result)) {
          const isAbsolute = /^https?:\/\//.test(result.url);
          const target = result.target || '_blank';
          if (target === '_self') {
            // Same-tab navigation — relative paths resolve against the
            // page's origin (the SPA handles routes like /agent/<id>).
            window.location.assign(result.url);
          } else {
            // New tab — relative paths point at bridge endpoints
            // (`/logs`, `/api/...`), not the SPA's origin.
            const url = isAbsolute
              ? result.url
              : `${client.baseUrl.replace(/\/$/, '')}${result.url}`;
            window.open(url, target);
          }
          return;
        }
        if (isToastResponse(result)) {
          const opts: { description?: string; duration?: number } = {};
          if (result.description) opts.description = result.description;
          if (result.duration_ms != null) opts.duration = result.duration_ms;
          (toast[result.level ?? 'info'] ?? toast.info)(result.text, opts);
          return;
        }
        if (isConsoleResponse(result)) {
          // In chat mode (a session is active) append as a normal
          // timeline item — same UI surface as a python code block.
          // On the landing page (no active session) fall back to the
          // floating inline console under the input.
          const sid = currentSessionIdRef.current;
          if (sid) {
            const c = getOrCreateChat(sid);
            const item: ConsoleTimelineItem = {
              kind: 'console',
              id: `console:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
              sections: result.sections,
            };
            c.timeline = [...c.timeline, item];
            bumpIfCurrent(sid);
          } else {
            immediateResultRef.current.set(result);
          }
          return;
        }

        const c = getOrCreateChat(result.session_id);
        c.isRunning = true;
        c.runId = result.run_id;
        c.seq = -1;
        bump();

        if (!sessionId) {
          hydrateChat(result.session_id, { model: model ?? null });
          pendingModelRef.current = null;
          onSessionCreatedRef.current?.(result.session_id);
        }

        connectToSessionRef.current(result.session_id, -1);
      })();
    },
    [getOrCreateChat, hydrateChat, bump, bumpIfCurrent, client],
  );

  // -----------------------------------------------------------------------
  // stop — just tell the server; the SSE stream will receive cancelled+done
  // -----------------------------------------------------------------------

  const stop = useCallback(async () => {
    const sessionId = currentSessionIdRef.current;
    if (!sessionId) return;
    if (!client) return;
    try {
      await client.cancelSessionRun(sessionId);
    } catch {
      // Best-effort cancel
    }
  }, [client]);

  // -----------------------------------------------------------------------
  // setModel
  // -----------------------------------------------------------------------

  const setModel = useCallback(
    (model: string) => {
      const sessionId = currentSessionIdRef.current;
      if (sessionId) {
        const chat = getOrCreateChat(sessionId);
        chat.model = model;
        bump();
      } else {
        pendingModelRef.current = model;
      }
    },
    [getOrCreateChat, bump],
  );

  // -----------------------------------------------------------------------
  // reconnectIfNeeded — ensures the per-session content stream is open.
  // -----------------------------------------------------------------------

  const reconnectIfNeeded = useCallback(
    (sessionId: string) => {
      const chat = getOrCreateChat(sessionId);
      const afterSeq = chat.isRunning ? chat.seq : -1;
      connectToSessionRef.current(sessionId, afterSeq);
    },
    [getOrCreateChat],
  );

  const ensureSessionStream = useCallback(
    (sessionId: string, _activeRunId: string | null) => {
      // If a stream is already open, let it deliver events.  Tearing down
      // and reconnecting here races with event delivery: the disconnect
      // aborts the stream before it can deliver the events that would
      // resolve the run ID mismatch, causing an infinite reconnect loop.
      if (_sseAborts.has(sessionId)) return;

      const chat = getOrCreateChat(sessionId);
      connectToSessionRef.current(sessionId, chat.isRunning ? chat.seq : -1);
    },
    [getOrCreateChat],
  );

  // -----------------------------------------------------------------------
  // Tab visibility: reconnect SSE when the tab becomes visible again.
  // -----------------------------------------------------------------------

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;

      const sessionId = currentSessionIdRef.current;
      if (!sessionId) return;

      const chat = getOrCreateChatRef.current(sessionId);
      disconnectSSE(sessionId);
      connectToSessionRef.current(
        sessionId,
        chat.isRunning ? chat.seq : -1,
      );
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // -----------------------------------------------------------------------
  // Session switching: disconnect old SSE when the URL changes
  // -----------------------------------------------------------------------

  const prevSessionIdRef = useRef<string | null>(null);

  useEffect(() => {
    const prevId = prevSessionIdRef.current;
    prevSessionIdRef.current = currentSessionId;

    if (prevId && prevId !== currentSessionId) {
      disconnectSSE(prevId);
    }
  }, [currentSessionId]);

  const teardownSession = useCallback(
    (sessionId: string) => {
      disconnectSSE(sessionId);
      removeChat(sessionId);
    },
    [removeChat],
  );

  const syncRunStopped = useCallback(
    (sessionId: string) => {
      const chat = getOrCreateChat(sessionId);
      if (!chat.isRunning) return;
      chat.isRunning = false;
      chat.agentMessageId = null;
      chat.runningCodeExecIds.clear();
      chat.bufferedAgentTextDelta = '';
      chat.workingStatus = '';
      chat.isStreamingAgentText = false;
      bumpIfCurrent(sessionId);
    },
    [getOrCreateChat, bumpIfCurrent],
  );

  const clearError = useCallback(
    (sessionId: string) => {
      const chat = getChat(sessionId);
      if (!chat || chat.error == null) return;
      chat.error = null;
      bumpIfCurrent(sessionId);
    },
    [getChat, bumpIfCurrent],
  );

  const markAuthComplete = useCallback(
    (sessionId: string, provider: string) => {
      const chat = getOrCreateChat(sessionId);
      chat.timeline = chat.timeline.map((item: TimelineItem) => {
        if (item.kind === 'auth_request' && item.provider === provider) {
          return { ...item, status: 'success' as const };
        }
        return item;
      });
      bumpIfCurrent(sessionId);
    },
    [getOrCreateChat, bumpIfCurrent],
  );

  return {
    activeChatId,
    activeChat,
    sendInFlight,
    getChat,
    hydrateChat: hydrateChat as (id: string, opts?: ChatStoreOpts) => void,
    removeChat,
    sendMessage,
    stop,
    setModel,
    reconnectIfNeeded,
    ensureSessionStream,
    teardownSession,
    syncRunStopped,
    markAuthComplete,
    clearError,
  };
}
